import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/apiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const EditSOA = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(null);

  useEffect(() => {
    fetchSOA();
  }, [id]);

  const fetchSOA = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/soa/${id}`);
      setFormData(response.data);
    } catch (error) {
      toast.error('Failed to load Statement of Account');
    } finally {
      setLoading(false);
    }
  };

  const handleTransactionChange = (index, field, value) => {
    const updated = [...formData.transactions];
    updated[index] = { ...updated[index], [field]: field === 'debit' || field === 'credit' ? parseFloat(value) || 0 : value };
    
    // Recalculate running balance
    let running = 0;
    updated.forEach(txn => {
      running += (txn.debit || 0) - (txn.credit || 0);
      txn.balance = running;
    });

    const totalInvoiced = updated.reduce((sum, t) => sum + (t.debit || 0), 0);
    const totalPaid = updated.reduce((sum, t) => sum + (t.credit || 0), 0);

    setFormData({
      ...formData,
      transactions: updated,
      total_invoiced: totalInvoiced,
      total_paid: totalPaid,
      balance_due: totalInvoiced - totalPaid
    });
  };

  const addTransaction = () => {
    setFormData({
      ...formData,
      transactions: [...formData.transactions, {
        date: new Date().toISOString().split('T')[0],
        document_number: '',
        document_type: 'Invoice',
        description: '',
        debit: 0,
        credit: 0,
        balance: 0
      }]
    });
  };

  const removeTransaction = (index) => {
    const updated = formData.transactions.filter((_, i) => i !== index);
    let running = 0;
    updated.forEach(txn => {
      running += (txn.debit || 0) - (txn.credit || 0);
      txn.balance = running;
    });
    const totalInvoiced = updated.reduce((sum, t) => sum + (t.debit || 0), 0);
    const totalPaid = updated.reduce((sum, t) => sum + (t.credit || 0), 0);
    setFormData({ ...formData, transactions: updated, total_invoiced: totalInvoiced, total_paid: totalPaid, balance_due: totalInvoiced - totalPaid });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await axios.put(`${BACKEND_URL}/api/soa/${id}`, {
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        customer_address: formData.customer_address,
        start_date: formData.start_date,
        end_date: formData.end_date,
        transactions: formData.transactions,
        total_invoiced: formData.total_invoiced,
        total_paid: formData.total_paid,
        balance_due: formData.balance_due
      });
      if (response.data.success) {
        toast.success('Statement of Account updated');
        navigate(`/admin/billing/soa/${id}`);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update'));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !formData) {
    return (
      <div className="min-h-screen bg-gray-50 pt-40 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-40">
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate(`/admin/billing/soa/${id}`)}>
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Edit {formData.document_number}</h1>
              <p className="text-gray-600 mt-1">Modify Statement of Account</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <form onSubmit={handleSubmit} className="max-w-5xl mx-auto space-y-6">
          {/* Customer Info */}
          <Card>
            <CardHeader><CardTitle>Customer Information</CardTitle></CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Customer Name</Label>
                  <Input value={formData.customer_name} onChange={(e) => setFormData({...formData, customer_name: e.target.value})} data-testid="edit-soa-customer-name" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input value={formData.customer_email} onChange={(e) => setFormData({...formData, customer_email: e.target.value})} data-testid="edit-soa-customer-email" />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={formData.customer_phone} onChange={(e) => setFormData({...formData, customer_phone: e.target.value})} />
                </div>
                <div>
                  <Label>Address</Label>
                  <Input value={formData.customer_address || ''} onChange={(e) => setFormData({...formData, customer_address: e.target.value})} />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div>
                  <Label>Start Date</Label>
                  <Input type="date" value={formData.start_date} onChange={(e) => setFormData({...formData, start_date: e.target.value})} />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input type="date" value={formData.end_date} onChange={(e) => setFormData({...formData, end_date: e.target.value})} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Transactions */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Transactions</CardTitle>
                <Button type="button" variant="outline" size="sm" onClick={addTransaction} data-testid="add-transaction-btn">
                  <Plus size={16} className="mr-1" /> Add Row
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="p-2 text-left">Date</th>
                      <th className="p-2 text-left">Document</th>
                      <th className="p-2 text-left">Type</th>
                      <th className="p-2 text-left">Description</th>
                      <th className="p-2 text-right">Debit</th>
                      <th className="p-2 text-right">Credit</th>
                      <th className="p-2 text-right">Balance</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.transactions.map((txn, i) => (
                      <tr key={i} className={`border-b ${txn.document_type === 'Payment' ? 'bg-green-50' : ''}`}>
                        <td className="p-1"><Input type="date" value={txn.date} onChange={(e) => handleTransactionChange(i, 'date', e.target.value)} className="h-8 text-xs" /></td>
                        <td className="p-1"><Input value={txn.document_number} onChange={(e) => handleTransactionChange(i, 'document_number', e.target.value)} className="h-8 text-xs" /></td>
                        <td className="p-1">
                          <select value={txn.document_type} onChange={(e) => handleTransactionChange(i, 'document_type', e.target.value)} className="h-8 text-xs border rounded px-1 w-full">
                            <option value="Invoice">Invoice</option>
                            <option value="Payment">Payment</option>
                          </select>
                        </td>
                        <td className="p-1"><Input value={txn.description} onChange={(e) => handleTransactionChange(i, 'description', e.target.value)} className="h-8 text-xs" /></td>
                        <td className="p-1"><Input type="number" step="0.01" value={txn.debit || ''} onChange={(e) => handleTransactionChange(i, 'debit', e.target.value)} className="h-8 text-xs text-right w-24" /></td>
                        <td className="p-1"><Input type="number" step="0.01" value={txn.credit || ''} onChange={(e) => handleTransactionChange(i, 'credit', e.target.value)} className="h-8 text-xs text-right w-24" /></td>
                        <td className="p-2 text-right font-semibold text-xs">${(txn.balance || 0).toFixed(2)}</td>
                        <td className="p-1">
                          <Button type="button" variant="ghost" size="sm" className="text-red-500 h-8 w-8 p-0" onClick={() => removeTransaction(i)}>
                            <Trash2 size={14} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Summary */}
              <div className="mt-4 p-4 bg-gray-50 rounded-lg flex gap-8 justify-end text-sm">
                <div><span className="text-gray-600">Total Invoiced:</span> <span className="font-bold">${formData.total_invoiced.toFixed(2)}</span></div>
                <div><span className="text-gray-600">Total Paid:</span> <span className="font-bold text-green-600">${formData.total_paid.toFixed(2)}</span></div>
                <div><span className="text-gray-600">Balance Due:</span> <span className={`font-bold ${formData.balance_due > 0 ? 'text-red-600' : 'text-green-600'}`}>${formData.balance_due.toFixed(2)}</span></div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate(`/admin/billing/soa/${id}`)}>Cancel</Button>
            <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={saving} data-testid="save-soa-btn">
              {saving ? <span className="flex items-center gap-2"><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Saving...</span>
                : <span className="flex items-center gap-2"><Save size={20} /> Save Changes</span>}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
